import React, { useState } from 'react';
import { pdf, Document, Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer';
import { FaSpinner } from 'react-icons/fa';
import { FiDownload } from 'react-icons/fi';
import ClassifiedCard from './ClassifiedCard';

/* Font */
try {
  Font.register({
    family: 'Arial',
    fonts: [
      { src: '/fonts/arial.ttf', fontWeight: 200 },
      { src: '/fonts/ARIBL0.ttf', fontWeight: 500 },
    ],
  });
} catch (e) {
  console.warn('Font load failed', e);
}

/* Page constants */
const PAGE_WIDTH = 935.43;
const PAGE_HEIGHT = 1474.02;

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Arial',
    width: PAGE_WIDTH,
    height: PAGE_HEIGHT,
    padding: 10,
  },
  header: {
    fontSize: 24,
    fontWeight: 500,
    textAlign: 'center',
    marginBottom: 6,
  },
  row: {
    flexDirection: 'row',
    width: '100%',
  },
  column: {
    marginHorizontal: 2,
  },
});

/* ================= PDF DOCUMENT ================= */
const ClassifiedsDocument = ({ data }) => {
  if (!data || !data.columns || !data.total_pages) return null;

  return (
    <Document>
      {Array.from({ length: data.total_pages }, (_, pIdx) => {
        const pageNo = pIdx + 1;

        const pageColumns = data.columns
          .filter(c => c.page === pageNo && c.column >= 0 && c.column < 8)
          .sort((a, b) => a.column - b.column); // Keep natural order (1 → 8)

        return (
          <Page
            key={pageNo}
            style={[styles.page, { direction: 'rtl' }]}
            size={{ width: PAGE_WIDTH, height: PAGE_HEIGHT }}
          >
            <Text style={[styles.header, { textAlign: 'center' }]}>
              Matrimonial Classifieds – Page {pageNo}
            </Text>

            {/* This makes layout RTL without changing column numbers */}
            <View style={[styles.row, { flexDirection: 'row-reverse' }]}>
              {pageColumns.map(col => (
                <View key={col.column} style={styles.column}>
                  {col.cards?.map((item, idx) => (
                    <View key={item?.id || item?.crn || idx} style={{ marginBottom: 8 }}>
                      <ClassifiedCard item={item} />
                    </View>
                  ))}
                </View>
              ))}
            </View>
          </Page>
        );
      })}
    </Document>
  );
};

/* ================= UI WRAPPER ================= */
const MatrimonialClassifiedsPDF = ({ data, title }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleDownload = async () => {
    setError(null);

    if (!data || !data.columns || data.columns.length === 0) {
      setError('No valid data to generate PDF');
      return;
    }

    setLoading(true);
    try {
      const blob = await pdf(<ClassifiedsDocument data={data} />).toBlob();
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = 'matrimonial-classifieds.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();

      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      console.error(e);
      setError('PDF generation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <button className="text-white bg-green-600 hover:bg-green-700 border-green-600 p-1 rounded w-full" 
      onClick={handleDownload} 
      disabled={loading} 
      >
        {loading ? (
            <span className="flex flex-wrap items-center gap-x-2">
              <FaSpinner size={18} /> {title}
            </span>
          ) : (
            <span className="flex flex-wrap items-center gap-x-2">
              <FiDownload size={18} /> {title}
            </span>
          )}
      </button>
      {error && <div style={{ color: 'red' }}>{error}</div>}
    </div>
  );
};

export default MatrimonialClassifiedsPDF;
